<template>
  <div class="quiz-container">
    <div class="quiz-progress">
      <div
        class="progress-dot"
        v-for="s in totalSteps"
        :key="s"
        :class="{ active: s === step, completed: s < step }"
      ></div>
    </div>

    <!-- Step 1: Name + sign-off -->
    <div v-if="step === 1" class="quiz-step">
      <h2>What do people actually call you?</h2>
      <div class="field-group">
        <input
          type="text"
          v-model="senderName"
          maxlength="60"
          placeholder="The name your people use"
          class="text-input"
        />
      </div>
      <p class="step-hint">How do you sign off? (optional)</p>
      <div class="field-group">
        <input
          type="text"
          v-model="signOff"
          maxlength="40"
          placeholder="xoxo / — J / nothing, ever"
          class="text-input"
        />
      </div>
      <div class="step-actions">
        <button class="button secondary" @click="$emit('cancel')">Cancel</button>
        <button class="button primary" :disabled="!senderName.trim()" @click="step = 2">Next</button>
      </div>
    </div>

    <!-- Step 2: How do you show love? -->
    <div v-if="step === 2" class="quiz-step">
      <h2>How do you show love?</h2>
      <div class="picker-grid">
        <button
          v-for="opt in loveOptions"
          :key="opt.value"
          class="picker-button wide"
          :class="{ selected: loveStyle === opt.value }"
          @click="loveStyle = opt.value"
        >
          {{ opt.label }}
        </button>
      </div>
      <div class="step-actions">
        <button class="button secondary" @click="step = 1">Back</button>
        <button class="button primary" :disabled="!loveStyle" @click="step = 3">Next</button>
      </div>
    </div>

    <!-- Step 3: What postcard would YOU want? -->
    <div v-if="step === 3" class="quiz-step">
      <h2>What postcard would YOU want to get?</h2>
      <div class="picker-grid">
        <button
          v-for="opt in postcardOptions"
          :key="opt.value"
          class="picker-button wide"
          :class="{ selected: postcardStyle === opt.value }"
          @click="postcardStyle = opt.value"
        >
          {{ opt.label }}
        </button>
      </div>
      <div class="step-actions">
        <button class="button secondary" @click="step = 2">Back</button>
        <button class="button primary" :disabled="!postcardStyle" @click="step = 4">Next</button>
      </div>
    </div>

    <!-- Step 4: How you text -->
    <div v-if="step === 4" class="quiz-step">
      <h2>When you text your best friend, it sounds like...</h2>
      <div class="picker-grid">
        <button
          v-for="opt in textOptions"
          :key="opt.value"
          class="picker-button wide"
          :class="{ selected: textStyle === opt.value }"
          @click="textStyle = opt.value"
        >
          {{ opt.label }}
        </button>
      </div>
      <div class="step-actions">
        <button class="button secondary" @click="step = 3">Back</button>
        <button class="button primary" :disabled="!textStyle" @click="step = 5">Next</button>
      </div>
    </div>

    <!-- Step 5: Verbatim register sample -->
    <div v-if="step === 5" class="quiz-step">
      <h2>Type the birthday text you'd send your best friend. Right now. Don't overthink it.</h2>
      <p class="step-hint">Optional — but this is the one that makes it sound like you.</p>
      <div class="field-group">
        <textarea
          v-model="sampleText"
          maxlength="500"
          placeholder="happy birthday you absolute legend…"
          class="text-input"
          rows="4"
        ></textarea>
      </div>
      <div class="step-actions">
        <button class="button secondary" @click="step = 4">Back</button>
        <button class="button primary" @click="step = 6">Next</button>
      </div>
    </div>

    <!-- Step 6: Voice goals -->
    <div v-if="step === 6" class="quiz-step">
      <h2>If this app could write like you, it should...</h2>
      <p class="step-hint">Pick 1 to 2</p>
      <div class="chip-grid">
        <button
          v-for="goal in voiceGoalOptions"
          :key="goal"
          class="chip-button"
          :class="{ selected: voiceGoals.includes(goal) }"
          @click="toggleGoal(goal)"
        >
          {{ goal }}
        </button>
      </div>
      <div class="step-actions">
        <button class="button secondary" @click="step = 5">Back</button>
        <button class="button primary" :disabled="voiceGoals.length === 0" @click="step = 7">Next</button>
      </div>
    </div>

    <!-- Step 7: Anything else (optional) -->
    <div v-if="step === 7" class="quiz-step">
      <h2>Anything else?</h2>
      <p class="step-hint">Optional — tell us anything that helps us sound like you.</p>
      <div class="field-group">
        <textarea
          v-model="selfNote"
          placeholder="Words you overuse, things you'd never say, how you start texts…"
          class="text-input"
          rows="4"
        ></textarea>
      </div>
      <div class="step-actions">
        <button class="button secondary" @click="step = 6">Back</button>
        <button class="button primary" @click="submit" :disabled="saving">Done</button>
      </div>
    </div>

    <Spinner :show="saving" message="Saving..." />
  </div>
</template>

<script>
import Spinner from '@/components/SpinnerComp.vue';
import { saveSenderOnboarding } from '@/api';

export default {
  name: 'SenderOnboardingQuiz',
  components: { Spinner },
  emits: ['complete', 'cancel'],
  data() {
    return {
      step: 1,
      saving: false,
      senderName: '',
      signOff: '',
      loveStyle: '',
      postcardStyle: '',
      textStyle: '',
      sampleText: '',
      voiceGoals: [],
      selfNote: '',
      loveOptions: [
        { value: 'love_roast', label: 'I roast you, but I\'d take a bullet for you' },
        { value: 'love_essay', label: 'I\'ll text you a 47-message essay at 2am' },
        { value: 'love_soup', label: 'I show up with soup and don\'t talk about it' },
        { value: 'love_playlist', label: 'I make you a playlist and never mention it' },
        { value: 'love_outloud', label: 'I say \'I love you\' out loud, fight me' },
      ],
      postcardOptions: [
        { value: 'postcard_weird', label: 'Something so weird I laugh-cry on the bus' },
        { value: 'postcard_sincere', label: 'A sincere one that I pretend didn\'t make me emotional' },
        { value: 'postcard_dumb', label: 'A dumb joke with heart behind it' },
        { value: 'postcard_dark', label: 'Something dark and funny that only I\'d understand' },
        { value: 'postcard_whatever', label: 'Whatever, just knowing someone thought of me' },
      ],
      textOptions: [
        { value: 'text_minimal', label: 'lmao / minimal / emoji-only' },
        { value: 'text_punctuation', label: 'Full sentences with punctuation' },
        { value: 'text_voicenotes', label: 'Voice notes and stream of consciousness' },
        { value: 'text_memes', label: 'Memes with zero context' },
        { value: 'text_renaissance', label: 'Like a Renaissance letter but lowercase' },
      ],
      voiceGoalOptions: [
        'Never be too serious',
        'Be warm but not cheesy',
        'Be a little weird',
        'Sound like a real person, not a brand',
        'Commit to the bit',
        'Actually be funny, not \'funny\'',
      ],
    };
  },
  computed: {
    totalSteps() {
      return 7;
    },
  },
  methods: {
    toggleGoal(goal) {
      const idx = this.voiceGoals.indexOf(goal);
      if (idx > -1) {
        this.voiceGoals.splice(idx, 1);
      } else if (this.voiceGoals.length < 2) {
        this.voiceGoals.push(goal);
      }
    },
    async submit() {
      this.saving = true;
      try {
        const responses = {
          senderName: this.senderName.trim(),
          signOff: this.signOff.trim(),
          loveStyle: this.loveStyle,
          postcardStyle: this.postcardStyle,
          textStyle: this.textStyle,
          sampleText: this.sampleText.trim(),
          voiceGoals: this.voiceGoals,
          selfNote: this.selfNote,
        };
        const result = await saveSenderOnboarding({ responses });
        this.$emit('complete', result.data);
      } catch (error) {
        this.$toast.error('Failed to save. Please try again.');
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
.quiz-container {
  max-width: 560px;
  margin: 0 auto;
  padding: 20px;
}

.quiz-progress {
  display: flex;
  justify-content: center;
  gap: 8px;
  margin-bottom: 24px;
}

.progress-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background-color: #e0e0e0;
  transition: background-color 0.3s;
}

.progress-dot.active {
  background-color: var(--hollywood-cerise);
  transform: scale(1.2);
}

.progress-dot.completed {
  background-color: var(--accent2-color);
}

.quiz-step h2 {
  margin: 0 0 8px;
  color: var(--rich-black);
  font-size: 1.4rem;
}

.step-hint {
  margin: 0 0 16px;
  color: var(--violet-lush);
  font-size: 0.9rem;
}

.field-group {
  margin-bottom: 16px;
}

.text-input {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #ccc;
  border-radius: 8px;
  font-size: 0.95rem;
  box-sizing: border-box;
}

.text-input:focus {
  outline: none;
  border-color: var(--hollywood-cerise);
}

.picker-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.picker-button {
  padding: 10px 18px;
  border: 2px solid #e0e0e0;
  border-radius: 8px;
  background-color: #fff;
  cursor: pointer;
  font-size: 0.9rem;
  transition: all 0.2s;
  color: var(--rich-black);
}

.picker-button:hover {
  border-color: var(--hollywood-cerise);
}

.picker-button.selected {
  border-color: var(--hollywood-cerise);
  background-color: #ffe6ed;
  color: var(--hollywood-cerise);
  font-weight: 600;
}

.picker-button.wide {
  flex: 1 1 calc(50% - 10px);
  min-width: 140px;
  text-align: center;
}

.chip-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 16px;
}

.chip-button {
  padding: 6px 14px;
  border: 2px solid #e0e0e0;
  border-radius: 20px;
  background-color: #fff;
  cursor: pointer;
  font-size: 0.85rem;
  transition: all 0.2s;
  color: var(--rich-black);
}

.chip-button:hover {
  border-color: var(--hollywood-cerise);
}

.chip-button.selected {
  border-color: var(--hollywood-cerise);
  background-color: #ffe6ed;
  color: var(--hollywood-cerise);
  font-weight: 600;
}

.step-actions {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  margin-top: 24px;
}

.step-actions .button {
  padding: 10px 20px;
  border: none;
  border-radius: 8px;
  font-size: 0.95rem;
  cursor: pointer;
  transition: background-color 0.3s;
}

.step-actions .button.primary {
  background-color: var(--accent2-color);
  color: white;
}

.step-actions .button.primary:hover {
  background-color: var(--accent-color);
}

.step-actions .button.primary:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.step-actions .button.secondary {
  background-color: var(--hollywood-cerise);
  color: white;
}

.step-actions .button.secondary:hover {
  background-color: #e41393;
}

@media (max-width: 768px) {
  .quiz-container {
    padding: 16px;
  }

  .picker-button.wide {
    flex: 1 1 100%;
  }
}
</style>
